import React, { useState, useEffect, useCallback } from "react";
import BasicRouter from "./BasicRouter";
import { loginPost, loginToken, setUserEntry } from "./server/Customer";
import { getCart } from "./server/CollectSite";
import { setToggleCart } from "./server/CartsOpen";
import { useSelector, useDispatch } from "react-redux";
import Header from "./components/header/Header";
import Footer from "./components/footer/Footer";
import Preload from "./components/Preload";
import OpenText from "./components/OpenText";
import { customerList, customerListClear } from "./server/ShoppingList";
import { SystemMessagePopUp } from "./components/containers/PopUp";
import { useLocation } from "react-router-dom";
import UserResetEmail from "./components/header/UserEmailReset";
import PopAddress from "./components/routs/address/PopAddress";
import ProductPageWithImg from "./components/routs/productPage/ProductPageWithImg";
import HeaderConnection from "./components/header/HeaderConnection";
import AdminDashboard from "./components/routs/adminDashboard/AdminDashboard";
import StatusBar from "./components/StatusBar";
import CartDrawer from "./components/cartDrawer/CartDrawer";
import { OpenTextGet } from "./server/OpenText";
import { updateMetaTags } from "./utils/metaTags";
import AuthDialog from "./components/header/AuthDialog";

const App = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { user, preload, role, userHaveReset, stationId } = useSelector(
    (state) => ({
      user: state.user.user,
      role: state.user.role,
      preload: state.openCart.preload,
      userHaveReset: state.user.userHaveReset,
      stationId: state.user.stationId,
    }),
  );

  const [canPay, setCanPay] = useState(true);
  const [startPopUp, setStartPopUp] = useState(true);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);
  const [showStationPopUp, setShowStationPopUp] = useState(true);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [allProduct, setAllProduct] = useState(true);

  const haveGetToken = useCallback(() => {
    if (userHaveReset) {
      return;
    }

    dispatch(setUserEntry([true, "login"]));

    const val = {
      userName: "guest",
      password: "a1234567",
      grant_type: "password",
    };
    dispatch(loginToken(val, false));
  }, [dispatch, userHaveReset]);

  const getToken = useCallback(() => {
    if (userHaveReset) {
      return;
    }

    if (!localStorage.getItem("jwtToken")) {
      haveGetToken();
    } else {
      const isGuest = localStorage.getItem("userRole")?.includes("guest");
      if (isGuest) {
        dispatch(setUserEntry([true, "login"]));
      }
      dispatch(loginPost(false));
    }
  }, [dispatch, haveGetToken, userHaveReset]);

  const getProducts = useCallback(() => {
    if (!role) {
      dispatch(getCart(stationId));
    }
  }, [dispatch, role, stationId]);

  useEffect(() => {
    getToken();
  }, [getToken]);

  // Fetch login message and update meta tags after token is available
  useEffect(() => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken) {
      return; // Don't fetch if no token
    }

    OpenTextGet("loginMessage")
      .then((response) => {
        if (response.data && response.data.length > 0) {
          const message = response.data[0].Text;
          if (message) {
            // Strip HTML tags and trim to reasonable length for meta description
            const plainText = message
              .replace(/<[^>]*>/g, "") // Remove HTML tags
              .replace(/\s+/g, " ") // Collapse whitespace
              .trim();
            const metaDescription = plainText.substring(0, 160); // Limit to 160 chars for SEO
            updateMetaTags(metaDescription);
          }
        }
      })
      .catch(() => {
        // Silent fail - keep default meta tags
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (!user || localStorage.getItem("userRole")?.includes("guest")) {
      dispatch(setToggleCart(false));
      dispatch(customerListClear());
    } else {
      getProducts();
      if (role) {
        dispatch(customerListClear());
      } else {
        dispatch(customerList());
      }
      if (user?.id) {
        setShippingAddress(user);
      }
    }
  }, [user, dispatch, role, getProducts]);

  const [productPopUp, setProductPopUp] = useState(false);
  const [selectedProd, setSelectedProd] = useState(null);
  const [pendingScrollSlug, setPendingScrollSlug] = useState(null);

  if (pathname.startsWith("/admin") && role) {
    return (
      <div className="admin-mode">
        <div className="header-wrapper">
          <HeaderConnection
            setAllProduct={setAllProduct}
            user={user}
            role={role}
            stationId={stationId}
            basicHeader={true}
            haveGetToken={haveGetToken}
          />
        </div>
        <AdminDashboard />
        {preload ? <Preload /> : null}
        <BasicRouter
          allProduct={allProduct}
          canPay={canPay}
          closePayPopup={() => setCanPay(false)}
          setCurrentSubCategory={setCurrentSubCategory}
          currentSubCategory={currentSubCategory}
          setProductPopUp={setProductPopUp}
          setSelectedProd={setSelectedProd}
        />
        <StatusBar />
      </div>
    );
  }

  const isGuest = localStorage.getItem("userRole")?.includes("guest");
  const showAddressPopUp = showStationPopUp && user && !role && !isGuest;

  return (
    <div>
      {showAddressPopUp ? (
        <PopAddress
          setShowStationPopUp={setShowStationPopUp}
          shippingAddress={shippingAddress}
        ></PopAddress>
      ) : null}
      {startPopUp && user && !role && !showAddressPopUp ? (
        <SystemMessagePopUp width="500px" close={() => setStartPopUp(false)}>
          <OpenText ShowHome={false} />
        </SystemMessagePopUp>
      ) : null}
      <Header
        setAllProduct={setAllProduct}
        currentSubCategory={currentSubCategory}
        haveGetToken={haveGetToken}
        setProductPopUp={setProductPopUp}
        setSelectedProd={setSelectedProd}
        setPendingScrollSlug={setPendingScrollSlug}
      />
      {preload ? <Preload /> : null}
      <main>
        <BasicRouter
          allProduct={allProduct}
          canPay={canPay}
          closePayPopup={() => setCanPay(false)}
          setCurrentSubCategory={setCurrentSubCategory}
          currentSubCategory={currentSubCategory}
          setProductPopUp={setProductPopUp}
          setSelectedProd={setSelectedProd}
          pendingScrollSlug={pendingScrollSlug}
          setPendingScrollSlug={setPendingScrollSlug}
        />
      </main>
      {productPopUp && (
        <ProductPageWithImg
          selectedProd={selectedProd}
          close={() => setProductPopUp(false)}
        />
      )}
      <CartDrawer
        setProductPopUp={setProductPopUp}
        setSelectedProd={setSelectedProd}
      />
      <StatusBar />
      <Footer />
      {userHaveReset ? (
        <UserResetEmail
          Email={userHaveReset.email}
          Password={userHaveReset.password}
          customerId={userHaveReset.customerId}
          emailPassword={userHaveReset.passwordMail}
        />
      ) : null}
      <AuthDialog />
    </div>
  );
};

export default App;
